<script setup>
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import moment from 'moment';
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification";
import Spinner from "@/components/ui/Spinner.vue"
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import DestinationEmptyPlaceholder from '@/components/Destination/DestinationEmptyPlaceholder.vue'
import DestinationList from '@/components/Destination/DestinationList.vue'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import AddDestinationModal from "@/components/modals/AddDestinationModal.vue"
import FormButton from '../../components/Atoms/FormButton.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import PageHeaderUsageMeta from '@/components/ui/PageHeaderUsageMeta.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

// TemplateRefs
const destinationSidebar = useTemplateRef('destinationSidebar')
// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const destinations = computed(() => store.getters['Destination/destinations'])
const hasDestinations = computed(() => store.getters['User/hasDestinations'])
const hasDestinationLimit = computed(() => store.getters['User/hasDestinationLimit'])

const destinationUsage = computed(() => {
  const meta = {
    used: destinations.value?.length || 0,
    label: 'destinations',
    labelSingular: 'destination',
  }
  if (hasDestinationLimit.value > 0) meta.limit = hasDestinationLimit.value
  return meta
})

onMounted(async () => {
  store.dispatch('Ui/setLoadingStatus', true)
  await store.dispatch('Streams/getStreams')
  await store.dispatch('Destination/getDestinations')
  await store.dispatch('Destination/getPlatforms')
  store.dispatch('Ui/setLoadingStatus', false)
})

// Methods
const showDestinationModal = () => {
  destinationSidebar.value.toggleSidebar()
}

</script>

<template>
  <div class="container detail-shell page-title-area px-4">
    <header class="mt-5">
      <div class="title flex justify-between items-center mb-4">
        <div class="flex items-center gap-x-3 min-w-0">
          <h1 class="page-title mb-0">Destination</h1>
          <!-- Saved destinations are not zoned: the library is the account's, so
               there is nothing to split between workspaces here. -->
          <PageHeaderUsageMeta v-if="!loadingStatus && destinationUsage" v-bind="destinationUsage" />
        </div>
        <Tooltip :active="destinations.length >= hasDestinationLimit" content="Contact Sales to add more destinations">
          <FormButton v-if="hasDestinations && hasDestinationLimit > 0"
            :disabled="destinations.length >= hasDestinationLimit" @click="showDestinationModal" size="lg">
            <icon-base width="14" height="14" widthViewBox="14" heightViewBox="14" icon-name="plus"
              classIcon="mr-2"><icon-plus /></icon-base>
            New Destination
          </FormButton>
        </Tooltip>
      </div>
    </header>
    <div v-if="loadingStatus" class="flex items-center justify-center w-full min-h-[70vh]" aria-busy="true">
      <Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
    </div>
    <template v-else>
      <DestinationList v-if="destinations.length > 0 && hasDestinations" />
      <DestinationEmptyPlaceholder v-else :action="showDestinationModal" />
    </template>
    <AddDestinationModal ref="destinationSidebar" />
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
